import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Account, Id } from '@hero/schema';
import { getAccountBalanceForViewer, getAccountCounterparty } from '@hero/core';
import { useAccountDrawerStore } from '@hero/hooks-shared';
import { BuildingIcon, WalletIcon } from '@hero/web-icons';
import { Card } from '../../../app/Card/Card';
import { Price } from '../../../app/Price';
import { AccountStatusBadge } from '../Status/AccountStatusBadge';

export interface AccountCardProps {
  account: Account;
  /**
   * Viewer's party id. Drives which side of `parties[]` we show
   * as the counterparty + the sign on the displayed balance.
   * When undefined the card falls back to a neutral
   * `parties[0] ↔ parties[1]` rendering.
   */
  viewerOrgId?: Id | string;
  className?: string;
}

/**
 * List card for a bilateral `Account`. Tapping the card opens the
 * `AccountDrawer` (URL-synced). One row per counterparty / currency.
 */
export const AccountCard: React.FC<AccountCardProps> = ({
  account,
  viewerOrgId,
  className,
}) => {
  const { t } = useTranslation(['m-account']);
  const openDrawer = useAccountDrawerStore(s => s.open);

  const counterparty = getAccountCounterparty(account, viewerOrgId);
  const counterpartyName =
    counterparty?.name ?? `${account.parties[0]?.name ?? ''} ↔ ${account.parties[1]?.name ?? ''}`;

  const viewerBalance = getAccountBalanceForViewer(account, viewerOrgId);
  const balance = viewerBalance ?? account.balance;
  const isZero = balance === 0;
  const isPositive = balance > 0;
  const priceColor = isZero
    ? 'text-gray-700'
    : isPositive
    ? 'text-green-600'
    : 'text-red-600';

  return (
    <Card onClick={() => openDrawer(String(account._id))} className={className}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700 min-w-0">
          <div className="flex items-center justify-center rounded-md bg-gray-50 p-1.5 flex-shrink-0">
            <BuildingIcon className="size-3.5 text-gray-400" />
          </div>
          <span className="truncate">{counterpartyName}</span>
        </div>
        <AccountStatusBadge status={account.status} />
      </div>

      <div className="flex items-center justify-between gap-3 mt-2">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <WalletIcon className="size-3 text-gray-400" />
          <span>{account.currency}</span>
        </div>
        <span className={`text-sm font-semibold ${priceColor}`}>
          {!isZero && !isPositive && '-'}
          <Price amount={Math.abs(balance)} currency={account.currency} />
        </span>
      </div>

      {isZero && (
        <p className="text-[11px] text-muted-foreground mt-1">
          {t('m-account:balance_card.settled', {
            defaultValue: 'Settled with {{name}}',
            name: counterpartyName,
          })}
        </p>
      )}
    </Card>
  );
};
